if (!customElements.get("cart-recommendations")) {
  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.wrapper = this.querySelector(".swiper-wrapper");
      this.abortController = new AbortController();
      this.initialize();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    async initialize() {
      if (!this.wrapper) {
        this.hide();
        return;
      }

      this.cartProductIds = new Set(
        (this.dataset.productIds || "").split(",").filter(Boolean),
      );
      this.cartProductTypes = new Set(
        (this.dataset.productTypes || "")
          .split("||")
          .map((type) => type.trim())
          .filter(Boolean),
      );
      this.renderedProductIds = new Set();
      this.deduplicateCards();

      await this.loadForCart("complementary");

      if (!this.isConnected) return;

      // Gdy żaden produkt w koszyku nie ma ustawionych produktów komplementarnych
      // w Search & Discovery, sięgamy po automatyczne rekomendacje Shopify.
      // Odfiltrowujemy przy tym te same typy produktów, co w koszyku, żeby obok
      // sofy nie wyświetlić kolejnej sofy.
      if (!this.wrapper.querySelector("product-card")) {
        await this.loadForCart("related", { excludeCartProductTypes: true });

        if (!this.isConnected) return;
      }

      if (!this.wrapper.querySelector("product-card")) {
        this.hide();
        return;
      }

      this.reveal();
    }

    loadForCart(intent, options) {
      return Promise.all(
        [...this.cartProductIds].map((productId) =>
          this.loadSearchAndDiscoveryRecommendations(
            productId,
            intent,
            options,
          ),
        ),
      );
    }

    deduplicateCards() {
      this.wrapper.querySelectorAll("product-card").forEach((card) => {
        const productId = String(card.dataset.productId || "");

        if (
          !productId ||
          this.cartProductIds.has(productId) ||
          this.renderedProductIds.has(productId)
        ) {
          card.closest(".swiper-slide")?.remove();
          return;
        }

        this.renderedProductIds.add(productId);
      });
    }

    async loadSearchAndDiscoveryRecommendations(
      productId,
      intent = "complementary",
      options = {},
    ) {
      const root = window.Shopify?.routes?.root || "/";
      const params = new URLSearchParams({
        product_id: productId,
        limit: "10",
        section_id: "cart-recommendations-content",
        intent,
      });

      try {
        const url = `${root}recommendations/products?${params.toString()}`;
        let response = await fetch(url, {
          signal: this.abortController.signal,
        });

        // Shopify potrafi chwilowo odpowiedzieć 503/429 na rekomendacje.
        // Bez ponowienia sekcja „Uzupełnij aranżację” znikała z koszyka.
        for (let attempt = 1; !response.ok && attempt <= 2; attempt++) {
          await new Promise((resolve) => setTimeout(resolve, 600 * attempt));
          if (this.abortController.signal.aborted) return;
          response = await fetch(url, {
            signal: this.abortController.signal,
            cache: "no-store",
          });
        }

        if (!response.ok) return;

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(
          html,
          "text/html",
        );
        const results = documentFragment.querySelector(
          "[data-cart-recommendation-results]",
        );

        results?.querySelectorAll(".swiper-slide").forEach((slide) => {
          const card = slide.querySelector("product-card");
          const recommendedProductId = String(
            card?.dataset.productId || "",
          );

          if (
            !recommendedProductId ||
            this.cartProductIds.has(recommendedProductId) ||
            this.renderedProductIds.has(recommendedProductId)
          ) {
            return;
          }

          if (options.excludeCartProductTypes) {
            const productType = (slide.dataset.productType || "").trim();

            if (productType && this.cartProductTypes.has(productType)) return;
          }

          this.renderedProductIds.add(recommendedProductId);
          this.wrapper.append(slide);
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("[CartRecommendations] Failed to load", error);
        }
      }
    }

    hide() {
      this.hidden = true;
      this.closest("[data-cart-recommendations]")?.setAttribute("hidden", "");
      this.closest(".drawer__footer")?.classList.add(
        "drawer__footer--no-recommendations",
      );
    }

    reveal() {
      this.hidden = false;
      this.closest("[data-cart-recommendations]")?.removeAttribute("hidden");
      this.closest(".drawer__footer")?.classList.remove(
        "drawer__footer--no-recommendations",
      );

      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
        this.style.opacity = 1;
      }, 100);
    }
  }

  customElements.define("cart-recommendations", CartRecommendations);
}
